import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { FuelConsumption } from '../../types/fuelConsumption';
import { fuelConsumptionService } from '../../services/fuelConsumptionService';

interface FuelEditDialogProps {
  open: boolean;
  fuelRecord: FuelConsumption | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const FuelEditDialog: React.FC<FuelEditDialogProps> = ({
  open,
  fuelRecord,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    date: new Date(),
    amount: 0,
    volume: 0,
    supplier: ''
  });

  useEffect(() => {
    if (fuelRecord) {
      setFormData({
        date: fuelRecord.date,
        amount: fuelRecord.amount,
        volume: fuelRecord.volume,
        supplier: fuelRecord.supplier
      });
      setError(null);
    }
  }, [fuelRecord]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      if (!fuelRecord?.id) {
        throw new Error(t('fuelReceipt.errors.missingData'));
      }

      // Validation des données
      if (formData.amount <= 0 || formData.volume <= 0) {
        throw new Error(t('fuelReceipt.errors.invalidAmount'));
      }

      if (!formData.supplier.trim()) {
        throw new Error(t('fuelReceipt.errors.missingSupplier'));
      }

      await fuelConsumptionService.update(fuelRecord.id, {
        date: formData.date,
        amount: formData.amount,
        volume: formData.volume,
        supplier: formData.supplier,
        vehicleId: fuelRecord.vehicleId,
        organizationId: fuelRecord.organizationId
      });

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating fuel consumption:', err);
      const errorMessage = err instanceof Error ? err.message : t('fuelReceipt.errors.updateFailed');
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('fuelReceipt.edit.title')}</DialogTitle>
      <DialogContent>
        <Box component="form" id="fuel-edit-form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <DatePicker
            label={t('fuelReceipt.form.date')}
            value={formData.date}
            onChange={(newValue) => {
              if (newValue) {
                setFormData(prev => ({ ...prev, date: newValue }));
              }
            }}
            disabled={isSaving}
            sx={{ mb: 2, width: '100%' }}
          />

          <TextField
            fullWidth
            label={t('fuelReceipt.form.amount')}
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
            margin="normal"
            inputProps={{ step: "0.01" }}
            disabled={isSaving}
          />

          <TextField
            fullWidth
            label={t('fuelReceipt.form.volume')}
            type="number"
            value={formData.volume}
            onChange={(e) => setFormData(prev => ({ ...prev, volume: parseFloat(e.target.value) || 0 }))}
            margin="normal"
            inputProps={{ step: "0.01" }}
            disabled={isSaving}
          />

          <TextField
            fullWidth
            label={t('fuelReceipt.form.supplier')}
            value={formData.supplier}
            onChange={(e) => setFormData(prev => ({ ...prev, supplier: e.target.value }))}
            margin="normal"
            disabled={isSaving}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          form="fuel-edit-form"
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
